import { RestrictedContext } from '@backoffice-frontend/restricted'
import {
  DARK_MODE_KEY,
  LAST_AUTHENTICATED_USER,
  MAP_FILTERS_VISIBLE_KEY,
  NAV_OPEN_FLAG_KEY,
  POOLING_MAP_TOGGLE_TRAFFIC_LAYER_KEY,
  REACT_I18NEXT_LANGUAGE_KEY,
  REGIONS_KEY,
  SERVICE_AREA_MANAGEMENT_SERVICE_AREA_ID_KEY,
} from '@backoffice-frontend/storage-keys'
import { Claims, FromBoumWireClaims } from '@moia-dev/moia-token-claims'
import type { AuthSession, AuthUser } from 'aws-amplify/auth'
import { jwtDecode } from 'jwt-decode'
import type { ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import store from 'store'
import {
  fetchAuthSession,
  fetchMFAPreference,
  getCurrentUser,
  Hub,
  signOut,
} from './amplify'
import { isAuthFailure } from './authErrors'
import { configureAmplify } from './configureAmplify'
import { type BackoffOptions, useBackoffRetry } from './useBackoffRetry'

type CheckAuthState = (options?: { forceRefresh?: boolean }) => Promise<void>

type AuthContextProps = {
  claims: Claims | null
  token: string | null
  isAuthenticated: boolean
  loading: boolean
  hasMFA: boolean | null
  checkAuthState: CheckAuthState
}
export const AuthContext = createContext<AuthContextProps | null>(null)

const VISIBILITY_CHECK_THROTTLE_MS = 30_000

// Bounded because every request refreshes the token on its own anyway
const MAX_AUTH_CHECK_ATTEMPTS = 6

const AUTH_CHECK_BACKOFF: BackoffOptions = {
  maxAttempts: MAX_AUTH_CHECK_ATTEMPTS,
  baseDelayMs: 1000,
  maxDelayMs: 30_000,
  onRetryScheduled: (attempt, delayMs) =>
    console.warn(
      `[Auth] Auth check failed, retrying in ${delayMs}ms (attempt ${attempt} of ${MAX_AUTH_CHECK_ATTEMPTS})`,
    ),
  onGiveUp: () =>
    console.warn(
      `[Auth] Auth check still failing after ${MAX_AUTH_CHECK_ATTEMPTS} attempts, keeping the session and waiting for the next trigger`,
    ),
}

type AuthContextProviderProps = {
  children: ReactNode
  userPoolClientId: string
}

export const AuthContextProvider = ({
  children,
  userPoolClientId,
}: AuthContextProviderProps) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [session, setSession] = useState<AuthSession | null>(null)
  const [hasMFA, setHasMFA] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)

  const isAuthenticated = !!user
  const token = session?.tokens?.idToken?.toString() ?? null

  const lastCheckAtRef = useRef(0)
  const retry = useBackoffRetry(AUTH_CHECK_BACKOFF)

  const checkAuthState: CheckAuthState = useCallback(
    async ({ forceRefresh = false } = {}) => {
      retry.cancel()
      lastCheckAtRef.current = Date.now()

      try {
        const [currentUser, authSession, mfaPreference] = await Promise.all([
          getCurrentUser(),
          fetchAuthSession({ forceRefresh }),
          // Resolved here so all three land in one state update: a window where
          // `hasMFA` is unknown while authenticated renders the setup screen
          // over a running app
          fetchMFAPreference().catch((error: unknown) => {
            console.warn('[Auth] Could not read the MFA preference', error)

            return null
          }),
        ])
        setUser(currentUser)
        setSession(authSession)
        if (mfaPreference) {
          setHasMFA(mfaPreference.preferred === 'TOTP')
        }
        resetPersistedUserSpecificValues(currentUser.username)
        retry.reset()
      } catch (error) {
        if (isAuthFailure(error)) {
          retry.reset()
          // signing out to make sure we have a clean state
          await signOut()
          setUser(null)
          setSession(null)
          setHasMFA(null)
        } else {
          console.warn(
            '[Auth] Auth check could not be completed, keeping the current session',
            error,
          )
          retry.schedule(() => void checkAuthState({ forceRefresh }))
        }
      } finally {
        setLoading(false)
      }
    },
    [retry],
  )

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'tokenRefresh':
          checkAuthState()
          break
        case 'tokenRefresh_failure':
          // Checking again immediately re-enters this handler through Amplify's
          // own dispatch, so anything retryable goes through the backoff
          if (isAuthFailure(payload.data.error)) {
            checkAuthState()
          } else {
            retry.schedule(() => void checkAuthState())
          }
          break
        case 'signedOut':
          setUser(null)
          setSession(null)
          break
      }
    })

    // We can only configure Amplify after we set up our auth listeners to avoid a race condition
    // where we miss the initial signIn events (https://github.com/aws-amplify/amplify-js/issues/13436)
    configureAmplify(userPoolClientId)

    // Force a fresh token on first page load instead of reusing a cached one
    checkAuthState({ forceRefresh: true })

    return unsubscribe
  }, [checkAuthState, retry, userPoolClientId])

  // Proactively refresh auth state when the app regains visibility.
  // On Android tablets, the screen sleeping suspends network and can expire tokens.
  // This ensures a fresh session is obtained before any API calls are made.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return
      }
      // A tablet wakes and sleeps all day; each check costs Cognito calls
      if (Date.now() - lastCheckAtRef.current < VISIBILITY_CHECK_THROTTLE_MS) {
        return
      }
      checkAuthState()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [checkAuthState])

  const claims = useMemo(
    () =>
      token
        ? Claims.fromWireClaims(jwtDecode(token), {
            defaultBOUM: {},
            defaultCognito: {},
            defaultEmployee: {},
            defaultOpenID: {},
          })
        : null,
    [token],
  )

  return (
    <AuthContext.Provider
      value={{
        claims,
        token,
        isAuthenticated,
        hasMFA,
        loading,
        checkAuthState,
      }}
    >
      <RestrictedContext.Provider
        value={claims?.boum || FromBoumWireClaims({}, {})}
      >
        {children}
      </RestrictedContext.Provider>
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

const resetPersistedUserSpecificValues = (currentUsername: string) => {
  const lastUser = store.get(LAST_AUTHENTICATED_USER)
  store.set(LAST_AUTHENTICATED_USER, currentUsername)

  const currentUserIsDifferent = lastUser && currentUsername !== lastUser
  if (currentUserIsDifferent) {
    store.remove(SERVICE_AREA_MANAGEMENT_SERVICE_AREA_ID_KEY)
    store.remove(MAP_FILTERS_VISIBLE_KEY)
    store.remove(POOLING_MAP_TOGGLE_TRAFFIC_LAYER_KEY)
    store.remove(DARK_MODE_KEY)
    store.remove(NAV_OPEN_FLAG_KEY)
    store.remove(REACT_I18NEXT_LANGUAGE_KEY)
    store.remove(REGIONS_KEY)
  }
}
